import { Grid, Typography, Box, Container } from "@mui/material";
import { useLocation } from "react-router-dom";
import Solutions from "./solutions";
import { useEffect } from "react";
import CloudQueueIcon from "@mui/icons-material/CloudQueue";
import Footer from "../Footer";
import { ThemeProvider } from "@mui/material/styles";
import theme from "../style/theme";

const solutionDetails = {
  "auto-scaling": {
    features: [
      "Eliminate manual intervention with predictive scaling policies",
      "Handle traffic surges without over provisioning",
      "Shrink your infrastructure during off-peak hours",
      "Prevent overuse of on-demand instances",
    ],
    heading: "Smart Auto Scaling",
    subHead: "Scale dynamically. Save instantly.",
    para: "We automatically scale your cloud infrastructure up and down in real-time based on live traffic data, CPU thresholds, and usage trends:",
    image: "/images/auto-scaling.png",
  },
  "spot-instance": {
    features: [
      "Automatically choose the most stable spot markets",
      "Switch to on-demand instances when spot capacity dries up",
      "Preemptively migrate workloads to ensure zero disruption",
      "Monitor interruptions and adjust strategies in real time",
    ],
    heading: "Spot Instance Optimization",
    subHead: "High performance at a fraction of the cost.",
    para: "We help you intelligently utilize AWS Spot Instances — known for being up to 90% cheaper:",
    image: "/images/spot-instance.png",
  },
  termination: {
    features: [
      "Identify VMs with low CPU, disk, and network activity",
      "Gracefully shut down or hibernate non-critical workloads",
      "Schedule shutdowns during weekends or non-business hours",
      "Generate alerts or reports before terminating resources (if desired)",
    ],
    heading: "Idle Resource Termination",
    subHead: "Stop paying for what you don't use.",
    para: "Our platform continuously scans for underutilized or idle instances and takes automatic action:",
    image: "/images/termination-bg.png",
  },
  scheduling: {
    features: [
      "Set daily schedules for non-prod environments",
      "Automatically power off machines after working hours",
      "Integrate with CI/CD pipelines to start/stop infra dynamically",
    ],
    heading: "Cost-Effective Scheduling",
    subHead: "Run your environments only when needed.",
    para: "You don't need your dev, staging, or test environments running 24/7. We automate their lifecycle based on time or events:",
    image: "/images/scheduling-bg.png",
  },
};

const SolutionSection = ({ id, data, index }) => (
  <Box
    id={id}
    sx={{
      py: 8,
      bgcolor: index % 2 === 0 ? "background.default" : "background.paper",
    }}
  >
    <Container maxWidth="lg">
      <Grid
        container
        spacing={8}
        alignItems="center"
        direction={index % 2 === 0 ? "row" : "row-reverse"}
      >
        <Grid item xs={12} md={6} width={{ lg: "60%" }}>
          <Typography
            variant="h3"
            sx={{ fontWeight: "bold", mb: 2, color: "primary.main" }}
          >
            {data.heading}
          </Typography>
          <Typography variant="h5" color="text.secondary" sx={{ mb: 3 }}>
            {data.subHead}
          </Typography>
          <Typography variant="body1" sx={{ mb: 3 }}>
            {data.para}
          </Typography>
          {data.features.map((feature, idx) => (
            <Box
              key={idx}
              sx={{ display: "flex", alignItems: "center", mb: 1.5 }}
            >
              <CloudQueueIcon sx={{ color: "secondary.main", mr: 1.5 }} />
              <Typography variant="body1">{feature}</Typography>
            </Box>
          ))}
        </Grid>
        <Grid
          item
          xs={12}
          md={6}
          width={{ lg: "30%" }}
          sx={{ textAlign: "center" }}
        >
          <img
            src={data.image}
            alt={data.heading}
            style={{ maxWidth: "100%", height: "auto", maxHeight: 450 }}
          />
        </Grid>
      </Grid>
    </Container>
  </Box>
);

export default function AutoScaling() {
  const location = useLocation();

  useEffect(() => {
    const path = location.pathname.replace("/use-case/", "");
    const section = document.getElementById(path);
    if (section) {
      section.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [location.pathname]);

  return (
    <ThemeProvider theme={theme}>
      <Solutions />
      {Object.entries(solutionDetails).map(([key, data], index) => (
        <SolutionSection key={key} id={key} data={data} index={index} />
      ))}
      <Footer />
    </ThemeProvider>
  );
}
